import { useState } from "react";
import Footer from "./Footer";
import * as LucideIcons from "lucide-react";
import SafeHTML from "../../../components/SafeHTML";
import EkycLoader from "./EkycLoader";
import useStickyButton from "../hooks/useStickyButton";
import isMobileDevice from "../../../utils/isMobileDevice";
import DesktopQrModal from "../../../components/DesktopQrModal";
import SecureCamera from "../../../components/SecureCamera/SecureCamera";

const Guideline = (props) => {
  const { positiveSampleImage, negativeSampleImage, myKadSide, onNext, uiConfig = {}, loading = false } = props;

  const { buttonContainerRef, sentinelRef, isSticky, buttonHeight } = useStickyButton();
  const [showDesktopNotice, setShowDesktopNotice] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const handleOpenCamera = () => {
    if (!isMobileDevice()) {
      setShowDesktopNotice(true);
      return;
    }

    setShowDesktopNotice(false);
    setShowCamera(true);
  };

  // Safe access
  const global = uiConfig?.Global || {};
  const guideline = uiConfig?.Guideline || {};
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const highlightTextColor = global?.highlightText?.fontColor || "#2563eb"; // blue-600
  const buttonBgColor = global?.button?.background || "#2563eb"; // blue-600
  const buttonTextColor = global?.button?.fontColor || "#ffffff"; // white
  const pageBgColor = global?.pageContainer?.background || "#ffffff"; // white
  const showFooter = global?.Footer?.Show !== undefined ? global.Footer.Show : true;

  const getTitleHTML = () => {
    const titleConfig = guideline?.Title?.Text;
    if (titleConfig) {
      const side = myKadSide === "front" ? "Front" : "Back";
      return titleConfig.replace(/\{myKadSide === "front" \? "Front" : "Back"\}/g, side);
    }
    return `<h2 style="font-size: 1.25rem; font-weight: bold; margin-bottom: 0.5rem; text-align: center; color: ${textColor};">
      Please take a clear photo of your
      <br />
      <span style="color: ${highlightTextColor};">MyKad (${myKadSide === "front" ? "front" : "back"})</span>
    </h2>`;
  };

  const getDescriptionHTML = () => {
    const descriptionConfig = guideline?.Description?.Text;
    if (descriptionConfig) {
      return descriptionConfig;
    }
    return `<p style="margin-bottom: 1.5rem; text-align: left; max-width: 65ch; color: ${textColor};">
      Position your MyKad correctly, ensure good lighting, and avoid glare or reflections. See our example below for
      guidance. You can place the MyKad on your palm to angle it if required.
    </p>`;
  };

  const getSampleImageConfig = (type) => {
    const sampleImage = guideline?.SampleImage?.[type] || {};
    const defaultImage = type === "Positive" ? positiveSampleImage : negativeSampleImage;
    const defaultCaption = type === "Positive" 
      ? "Good: No glare & reflection. Correct angle." 
      : "Bad: Glare & reflection.";
    const defaultIconColor = type === "Positive" ? "#16a34a" : "#dc2626";

    const configuredUrl = sampleImage?.Image?.Url;
    const imageUrl = (configuredUrl && configuredUrl.trim() !== '') ? configuredUrl : defaultImage;

    return {
      imageUrl: imageUrl,
      imageStyle: sampleImage?.Image?.Style || "height: auto; width: 20rem; max-height: 200px; object-fit: contain; border-radius: 0.5rem; box-shadow: 0 2px 8px rgba(0,0,0,0.5);",
      iconColor: sampleImage?.Icon?.Color || defaultIconColor,
      iconWidth: sampleImage?.Icon?.Width || 80,
      iconHeight: sampleImage?.Icon?.Height || 80,
      iconOpacity: sampleImage?.Icon?.Opacity !== undefined ? sampleImage.Icon.Opacity : 0.5,
      showIcon: sampleImage?.Icon?.Show !== undefined ? sampleImage.Icon.Show : true,
      captionText: sampleImage?.Caption?.Text || defaultCaption,
      captionStyle: sampleImage?.Caption?.Style || `font-size: 0.875rem; text-align: center; font-weight: bold; margin-top: 0.25rem; color: ${textColor};`
    };
  };

  const getButtonConfig = () => {
    const globalButton = global?.button || {};
    const buttonConfig = guideline?.Button || {};
    
    // Build base style from global button config
    const globalStyle = globalButton?.Style || `width: 66%; padding: 0.75rem 1rem; border-radius: 9999px; display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin: 0 auto; background: ${buttonBgColor}; color: ${buttonTextColor}; transition: transform 0.15s ease-in-out; border: none; cursor: pointer;`;
    
    // Parse global style
    const parsedGlobalStyle = globalStyle.split(';').reduce((acc, style) => {
      const [key, value] = style.split(':').map(s => s.trim());
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    // Parse and merge specific button style if it exists
    let finalStyle = { ...parsedGlobalStyle };
    if (buttonConfig?.Style) {
      const parsedButtonStyle = buttonConfig.Style.split(';').reduce((acc, style) => {
        const [key, value] = style.split(':').map(s => s.trim());
        if (key && value) {
          acc[key] = value;
        }
        return acc;
      }, {});
      finalStyle = { ...finalStyle, ...parsedButtonStyle };
    }
    
    // Convert back to CSS string
    const mergedStyleString = Object.entries(finalStyle)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ') + ';';
    
    return {
      text: buttonConfig?.Text || globalButton?.Text || "Continue",
      style: mergedStyleString,
      icon: {
        show: buttonConfig?.Icon?.Show !== undefined ? buttonConfig.Icon.Show : (globalButton?.Icon?.Show !== undefined ? globalButton.Icon.Show : true),
        position: buttonConfig?.Icon?.Position || globalButton?.Icon?.Position || "end",
        name: buttonConfig?.Icon?.Name || globalButton?.Icon?.Name || "ChevronRight",
        className: buttonConfig?.Icon?.ClassName || globalButton?.Icon?.ClassName || "w-5 h-5"
      }
    };
  };

  const handleSecureCapture = ({ file }) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = async () => {
      onNext(reader.result);
    };
  };

  if (loading) {
    return <EkycLoader message="Preparing verification..." variant="scanning" />;
  }

  return (
    <div
      style={{
        background: pageBgColor,
        padding: "1rem",
        // borderTopLeftRadius: "0.75rem",
        // borderTopRightRadius: "0.75rem",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        height: "100%",
      }}
    >
      <SecureCamera
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleSecureCapture}
        frameType="ID"
        title={`Place your MyKad (${myKadSide === "front" ? "front" : "back"}) within the frame`}
        theme={{
          primaryColor: buttonBgColor,
          textColor: "#ffffff"
        }}
      />

      <SafeHTML html={getTitleHTML()} />

      <SafeHTML html={getDescriptionHTML()} />

      {/* Positive Image */}
      {(() => {
        const positiveConfig = getSampleImageConfig("Positive");
        return (
          <>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
              <div style={{ marginBottom: "0.5rem", display: "flex", justifyContent: "center" }}>
                <img src={positiveConfig.imageUrl} style={positiveConfig.imageStyle.split(';').reduce((acc, style) => {
                  const [key, value] = style.split(':').map(s => s.trim());
                  if (key && value) {
                    const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                    acc[camelKey] = value;
                  }
                  return acc;
                }, {})} />
              </div>
              {positiveConfig.showIcon && (
                <LucideIcons.CircleCheck
                  style={{
                    color: positiveConfig.iconColor,
                    position: "absolute",
                    top: "50%",
                    transform: "translateY(-50%)",
                    opacity: positiveConfig.iconOpacity,
                  }}
                  width={positiveConfig.iconWidth}
                  height={positiveConfig.iconHeight}
                />
              )}
            </div>
            <p style={positiveConfig.captionStyle.split(';').reduce((acc, style) => {
              const [key, value] = style.split(':').map(s => s.trim());
              if (key && value) {
                const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                acc[camelKey] = value;
              }
              return acc;
            }, {})}>
              {positiveConfig.captionText}
            </p>
          </>
        );
      })()}

      {/* Spacer */}
      <div style={{ marginTop: "1rem" }} />

      {/* Negative Image */}
      {(() => {
        const negativeConfig = getSampleImageConfig("Negative");
        return (
          <>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
              <div style={{ marginBottom: "0.5rem", display: "flex", justifyContent: "center" }}>
                <img src={negativeConfig.imageUrl} style={negativeConfig.imageStyle.split(';').reduce((acc, style) => {
                  const [key, value] = style.split(':').map(s => s.trim());
                  if (key && value) {
                    const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                    acc[camelKey] = value;
                  }
                  return acc;
                }, {})} />
              </div>
              {negativeConfig.showIcon && (
                <LucideIcons.XCircle
                  style={{
                    color: negativeConfig.iconColor,
                    position: "absolute",
                    top: "50%",
                    transform: "translateY(-50%)",
                    opacity: negativeConfig.iconOpacity,
                  }}
                  width={negativeConfig.iconWidth}
                  height={negativeConfig.iconHeight}
                />
              )}
            </div>
            <p style={negativeConfig.captionStyle.split(';').reduce((acc, style) => {
              const [key, value] = style.split(':').map(s => s.trim());
              if (key && value) {
                const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                acc[camelKey] = value;
              }
              return acc;
            }, {})}>
              {negativeConfig.captionText}
            </p>
          </>
        );
      })()}

      <div style={{ marginBottom: "2rem", flex: 1 }} />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Spacer to maintain space when button is sticky */}
      {isSticky && buttonHeight > 0 && (
        <div style={{ height: `${buttonHeight}px`, width: "100%" }} />
      )}

      {/* Button */}
      <div 
        ref={buttonContainerRef}
        style={{
          position: "sticky",
          bottom: "0",
          left: "0",
          right: "0",
          width: "100%",
          padding: "1rem",
          background: "transparent",
          zIndex: "20",
          transition: "all 0.3s ease-in-out",
          marginTop: "auto",
        }}
      >
        {(() => {
          const btnConfig = getButtonConfig();
          const IconComponent = btnConfig.icon.show && LucideIcons[btnConfig.icon.name] ? LucideIcons[btnConfig.icon.name] : null;
          
          const buttonStyle = btnConfig.style.split(';').reduce((acc, style) => {
            const [key, value] = style.split(':').map(s => s.trim());
            if (key && value) {
              const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
              acc[camelKey] = value;
            }
            return acc;
          }, {});

          return (
            <button
              style={{ ...buttonStyle, pointerEvents: 'auto' }}
              onClick={handleOpenCamera}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
              onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {btnConfig.icon.position === "start" && IconComponent && (
                <IconComponent className={btnConfig.icon.className} />
              )}
              <span>{btnConfig.text}</span>
              {btnConfig.icon.position === "end" && IconComponent && (
                <IconComponent className={btnConfig.icon.className} />
              )}
            </button>
          );
        })()}
      </div>

      <DesktopQrModal
        open={showDesktopNotice}
        onClose={() => setShowDesktopNotice(false)}
      />

      {showFooter ? (
        <Footer
          style={{
            fontSize: "0.75rem",
            color: textColor,
            marginTop: "1rem",
          }}
        />
      ) : (
        /* Minimal spacer to prevent sticky flickering when footer is hidden */
        <div style={{ height: "5px", flexShrink: 0 }} />
      )}
    </div>
  );
};

export default Guideline;
